/**
 * useUserActionTrail
 *
 * Tracks the last 25 user actions as a ring buffer and exposes them for
 * bug report context. All event listeners are set up with graceful
 * degradation — if any source is unavailable the rest still work.
 *
 * Exported helpers:
 *   dispatchApiErrorEvent   — call from the API service error interceptor
 *   dispatchEntityOpenEvent — call when an entity modal/panel opens
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// ─── Types ────────────────────────────────────────────────────────────────────

export type NavEntry = {
  type: 'nav';
  path: string;
  ts: string;
};

export type ClickEntry = {
  type: 'click';
  label: string;
  selector: string;
  ts: string;
};

export type FormSubmitEntry = {
  type: 'form_submit';
  formId: string;
  ts: string;
};

export type ApiErrorEntry = {
  type: 'api_error';
  status: number;
  url: string;
  ts: string;
};

export type EntityOpenEntry = {
  type: 'entity_open';
  entityType: string;
  entityId: string;
  ts: string;
};

export type ActionTrailEntry =
  | NavEntry
  | ClickEntry
  | FormSubmitEntry
  | ApiErrorEntry
  | EntityOpenEntry;

// ─── Custom event detail shapes ───────────────────────────────────────────────

interface ApiErrorEventDetail {
  status?: number;
  url?: string;
}

interface EntityOpenEventDetail {
  entityType?: string;
  entityId?: string;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_ENTRIES = 25;
const CLICK_THROTTLE_MS = 300;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function nowHHMMSS(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function isModalCloseButton(el: Element): boolean {
  // Skip generic close/dismiss buttons that add noise
  const label = el.getAttribute('aria-label') ?? '';
  if (/close|dismiss/i.test(label)) return true;
  const dataTrack = el.getAttribute('data-track') ?? '';
  if (/close|dismiss/i.test(dataTrack)) return true;
  return false;
}

function resolveClickTarget(target: EventTarget | null): Element | null {
  if (!(target instanceof Element)) return null;
  // Walk up to the nearest tracked element
  return target.closest('[data-track], button, a');
}

function getClickLabel(el: Element): string {
  return (
    el.getAttribute('aria-label') ||
    el.getAttribute('data-track') ||
    el.getAttribute('title') ||
    (el.textContent ?? '').trim().slice(0, 60) ||
    el.tagName.toLowerCase()
  );
}

function getClickSelector(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const id = el.getAttribute('id');
  if (id) return `${tag}#${id}`;
  const dataTrack = el.getAttribute('data-track');
  if (dataTrack) return `[data-track="${dataTrack}"]`;
  const cls = Array.from(el.classList)
    .slice(0, 2)
    .join('.');
  return cls ? `${tag}.${cls}` : tag;
}

// ─── Public dispatch helpers (for use outside React) ─────────────────────────

/** Dispatch from the API error interceptor after a failed request. */
export function dispatchApiErrorEvent(status: number | undefined, url: string): void {
  try {
    window.dispatchEvent(
      new CustomEvent<ApiErrorEventDetail>('pm:api-error', {
        detail: { status: status ?? 0, url },
      })
    );
  } catch {
    // Silently ignore — non-browser environments
  }
}

/** Dispatch when a tracked entity panel/modal opens. */
export function dispatchEntityOpenEvent(entityType: string, entityId: string | number): void {
  try {
    window.dispatchEvent(
      new CustomEvent<EntityOpenEventDetail>('pm:entity-open', {
        detail: { entityType, entityId: String(entityId) },
      })
    );
  } catch {
    // Silently ignore — non-browser environments
  }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export interface UseUserActionTrailReturn {
  /** Snapshot of the current trail, newest last. */
  getTrail: () => ActionTrailEntry[];
  /** Compact human-readable string for bug reports. */
  getTrailSummary: () => string;
}

export function useUserActionTrail(): UseUserActionTrailReturn {
  // Ring buffer stored in a ref to avoid triggering re-renders
  const bufferRef = useRef<ActionTrailEntry[]>([]);

  // ── Ring-buffer helpers ──────────────────────────────────────────────────
  const push = useCallback((entry: ActionTrailEntry) => {
    const buf = bufferRef.current;
    if (buf.length >= MAX_ENTRIES) {
      buf.shift();
    }
    buf.push(entry);
  }, []);

  // ── 1. Navigation tracking ───────────────────────────────────────────────
  // Gracefully degrade if called outside a Router context
  let locationPathname = '';
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const loc = useLocation();
    locationPathname = loc.pathname;
  } catch {
    // Not inside a Router — skip navigation tracking
  }

  useEffect(() => {
    if (!locationPathname) return;
    push({ type: 'nav', path: locationPathname, ts: nowHHMMSS() });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationPathname]);

  // ── 2. Click tracking (throttled, 300ms) ─────────────────────────────────
  useEffect(() => {
    let lastFired = 0;

    const handler = (e: MouseEvent) => {
      const now = Date.now();
      if (now - lastFired < CLICK_THROTTLE_MS) return;

      const el = resolveClickTarget(e.target);
      if (!el) return;
      if (isModalCloseButton(el)) return;

      lastFired = now;
      push({
        type: 'click',
        label: getClickLabel(el),
        selector: getClickSelector(el),
        ts: nowHHMMSS(),
      });
    };

    try {
      window.addEventListener('click', handler, { capture: true, passive: true });
    } catch {
      // Non-browser environment
    }

    return () => {
      try {
        window.removeEventListener('click', handler, { capture: true });
      } catch {
        // Ignore
      }
    };
  }, [push]);

  // ── 3. Form submit tracking ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const form = e.target instanceof HTMLFormElement ? e.target : null;
      const formId = form?.id ? `#${form.id}` : form?.getAttribute('name') ?? '(form)';
      push({ type: 'form_submit', formId, ts: nowHHMMSS() });
    };

    try {
      window.addEventListener('submit', handler, { capture: true, passive: true });
    } catch {
      // Non-browser environment
    }

    return () => {
      try {
        window.removeEventListener('submit', handler, { capture: true });
      } catch {
        // Ignore
      }
    };
  }, [push]);

  // ── 4. API error tracking ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ApiErrorEventDetail>).detail ?? {};
      push({
        type: 'api_error',
        status: typeof detail.status === 'number' ? detail.status : 0,
        url: detail.url ?? '',
        ts: nowHHMMSS(),
      });
    };

    try {
      window.addEventListener('pm:api-error', handler);
    } catch {
      // Non-browser environment
    }

    return () => {
      try {
        window.removeEventListener('pm:api-error', handler);
      } catch {
        // Ignore
      }
    };
  }, [push]);

  // ── 5. Entity open tracking ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<EntityOpenEventDetail>).detail ?? {};
      push({
        type: 'entity_open',
        entityType: detail.entityType ?? '',
        entityId: detail.entityId ?? '',
        ts: nowHHMMSS(),
      });
    };

    try {
      window.addEventListener('pm:entity-open', handler);
    } catch {
      // Non-browser environment
    }

    return () => {
      try {
        window.removeEventListener('pm:entity-open', handler);
      } catch {
        // Ignore
      }
    };
  }, [push]);

  // ── Public API ───────────────────────────────────────────────────────────

  const getTrail = useCallback((): ActionTrailEntry[] => {
    return [...bufferRef.current];
  }, []);

  const getTrailSummary = useCallback((): string => {
    return bufferRef.current
      .map((entry) => {
        switch (entry.type) {
          case 'nav':
            return `${entry.ts} nav ${entry.path}`;
          case 'click':
            return `${entry.ts} click "${entry.label}"`;
          case 'form_submit':
            return `${entry.ts} form_submit ${entry.formId}`;
          case 'api_error':
            return `${entry.ts} api_error ${entry.status} ${entry.url}`;
          case 'entity_open':
            return `${entry.ts} entity_open ${entry.entityType} ${entry.entityId}`;
        }
      })
      .join('\n');
  }, []);

  return { getTrail, getTrailSummary };
}
